/**
 * Global notification system — professional toast notifications.
 * Call from anywhere: showNotification('Message', 'success')
 */

type NotificationType = 'success' | 'error' | 'warning' | 'info';

const ICONS: Record<NotificationType, string> = {
  success: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
  error: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
  warning: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  info: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
};

const COLORS: Record<NotificationType, { bg: string; border: string; icon: string; text: string }> = {
  success: { bg: '#f0fdf4', border: '#bbf7d0', icon: '#16a34a', text: '#15803d' },
  error:   { bg: '#fef2f2', border: '#fecaca', icon: '#dc2626', text: '#b91c1c' },
  warning: { bg: '#fffbeb', border: '#fde68a', icon: '#d97706', text: '#92400e' },
  info:    { bg: '#eff6ff', border: '#bfdbfe', icon: '#2563eb', text: '#1d4ed8' },
};

const DARK_COLORS: Record<NotificationType, { bg: string; border: string; icon: string; text: string }> = {
  success: { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.25)', icon: '#34d399', text: '#6ee7b7' },
  error:   { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.25)', icon: '#f87171', text: '#fca5a5' },
  warning: { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.25)', icon: '#fbbf24', text: '#fcd34d' },
  info:    { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.25)', icon: '#60a5fa', text: '#93c5fd' },
};

// Stack management — track active notifications
let activeCount = 0;

export function showNotification(message: string, type: NotificationType = 'info') {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const colors = isDark ? DARK_COLORS[type] : COLORS[type];
  const offset = activeCount * 64;
  activeCount++;

  // The markup is ours alone; the message goes in as TEXT. It is often a
  // server's error string (a connector's remote server, a file name), and as
  // innerHTML it was an XSS sink. The close button is wired here rather than
  // with an inline onclick, which a Content-Security-Policy refuses to run.
  const el = document.createElement('div');
  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:10px;">
      <span style="color:${colors.icon};flex-shrink:0;display:flex;">${ICONS[type]}</span>
      <span class="app-toast__message" style="color:${colors.text};font-size:13.5px;font-weight:500;line-height:1.4;"></span>
      <button class="app-toast__close" style="margin-left:auto;background:none;border:none;color:${colors.icon};opacity:0.5;cursor:pointer;padding:2px;display:flex;">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
  `;
  (el.querySelector('.app-toast__message') as HTMLElement).textContent = message;
  el.querySelector('.app-toast__close')?.addEventListener('click', () => el.remove());
  el.className = 'app-toast';
  el.style.cssText = `
    position: fixed;
    top: ${16 + offset}px;
    right: 16px;
    min-width: 300px;
    max-width: 480px;
    padding: 12px 16px;
    border-radius: 10px;
    background: ${colors.bg};
    border: 1px solid ${colors.border};
    box-shadow: 0 4px 12px rgba(0,0,0,${isDark ? '0.3' : '0.08'}), 0 1px 3px rgba(0,0,0,${isDark ? '0.2' : '0.04'});
    z-index: 2147483647;
    transform: translateX(120%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease;
    font-family: inherit;
    backdrop-filter: blur(8px);
  `;

  document.body.appendChild(el);

  // Slide in
  requestAnimationFrame(() => {
    el.style.transform = 'translateX(0)';
  });

  // Auto dismiss with hover pause
  let timer: ReturnType<typeof setTimeout>;
  let hovered = false;

  const dismiss = () => {
    el.style.transform = 'translateX(120%)';
    el.style.opacity = '0';
    activeCount = Math.max(0, activeCount - 1);
    setTimeout(() => el.remove(), 300);
  };

  const startTimer = () => {
    timer = setTimeout(() => {
      if (!hovered) dismiss();
    }, type === 'error' ? 5000 : 3000);
  };

  el.addEventListener('mouseenter', () => {
    hovered = true;
    clearTimeout(timer);
  });

  el.addEventListener('mouseleave', () => {
    hovered = false;
    startTimer();
  });

  startTimer();
}

export default showNotification;
